import type { PresentableTaskResource } from '@timelyapp/integration-cloud-api-spec';
import type { Client } from 'types/client';
import type { ExternalHourLink, HourEditorClientFocusOnProjects } from 'types/hour';

interface ResolveClientFocusOnProjectsOptions {
  canUseClientFocusOnProjects: boolean;
  shouldUseClientFocusOnProjects: boolean;
  showAllProjectsForDraft: boolean;
  externalLinks: Array<ExternalHourLink>;
  tasks: Array<PresentableTaskResource> | null;
  clients: Array<Client>;
}

const JIRA_CUSTOM_FIELD_CLIENT_EXTERNAL_ID_PREFIX = 'custom-field-option:';
const JIRA_CUSTOM_FIELD_CLIENT_URI_PREFIX = 'tic://jira/clients/custom-field-option:';

const normalizeExternalId = (value: Client['external_id'] | string | null | undefined) =>
  value == null ? null : String(value);

const getLinkedTaskForExternalLinks = (
  externalLinks: Array<ExternalHourLink>,
  tasks: Array<PresentableTaskResource> | null
) => {
  const linkedUri = externalLinks[0]?.uri;
  if (!linkedUri || !tasks?.length) {
    return null;
  }

  return tasks.find(task => task.uri === linkedUri) ?? null;
};

const isJiraCustomFieldClientReference = (
  metadataClient: NonNullable<PresentableTaskResource['metadata']>['client']
) => {
  const externalId = normalizeExternalId(metadataClient?.external_id);
  const uri = typeof metadataClient?.uri === 'string' ? metadataClient.uri : null;

  return (
    externalId?.startsWith(JIRA_CUSTOM_FIELD_CLIENT_EXTERNAL_ID_PREFIX) ||
    uri?.startsWith(JIRA_CUSTOM_FIELD_CLIENT_URI_PREFIX) ||
    false
  );
};

export const resolveLinkedTaskCustomFieldClient = (
  clients: Array<Client>,
  task: PresentableTaskResource | null
) => {
  const metadataClient = task?.metadata?.client;
  if (!metadataClient || !isJiraCustomFieldClientReference(metadataClient)) {
    return null;
  }

  if (metadataClient.uri?.startsWith(JIRA_CUSTOM_FIELD_CLIENT_URI_PREFIX)) {
    const clientByUri = clients.find(client => client.tic?.uri === metadataClient.uri);
    if (clientByUri) {
      return clientByUri;
    }
  }

  const metadataExternalId = normalizeExternalId(metadataClient.external_id);
  if (
    !metadataExternalId ||
    !metadataExternalId.startsWith(JIRA_CUSTOM_FIELD_CLIENT_EXTERNAL_ID_PREFIX)
  ) {
    return null;
  }

  return (
    clients.find(client => normalizeExternalId(client.external_id) === metadataExternalId) ?? null
  );
};

export function resolveClientFocusOnProjects({
  canUseClientFocusOnProjects,
  shouldUseClientFocusOnProjects,
  showAllProjectsForDraft,
  externalLinks,
  tasks,
  clients,
}: ResolveClientFocusOnProjectsOptions): HourEditorClientFocusOnProjects {
  const linkedTask = getLinkedTaskForExternalLinks(externalLinks, tasks);
  const focusedClient = resolveLinkedTaskCustomFieldClient(clients, linkedTask);
  const isActive =
    canUseClientFocusOnProjects &&
    shouldUseClientFocusOnProjects &&
    !showAllProjectsForDraft &&
    focusedClient != null;
  const clientName = focusedClient?.name ?? null;

  const menuTooltip = !shouldUseClientFocusOnProjects
    ? 'Focus project lists when linked Jira work has a mapped client custom field.'
    : clientName == null
      ? 'Project lists will focus once linked Jira work identifies a single custom-field client.'
      : showAllProjectsForDraft
        ? `Showing all projects for this time entry. Focus would otherwise use ${clientName} from linked Jira custom field.`
        : `Project lists are focused to linked Jira custom-field client, currently ${clientName}.`;

  return {
    isAvailable: canUseClientFocusOnProjects,
    isEnabled: shouldUseClientFocusOnProjects,
    isActive,
    clientId: isActive && focusedClient ? focusedClient.id : null,
    menuTooltip,
    notificationMessage: isActive && clientName ? `Projects focused to ${clientName}` : null,
  };
}
